import type { Href } from 'expo-router';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getFeatureFlag, setUserProperties, trackEvent } from '@/modules/analytics';
import { createProgramInstance, programMetaInfoKey } from '@/modules/api';
import type { PrescribedPrograms } from '@/modules/api';
import type { User } from '@/types/user';

import { FEATURE_FLAGS } from '../constants/featureFlags';
import type { ResourceState } from '../types/resolution';
import { shouldShowTestimonial } from '../utils/shouldShowTestimonial';
import { uuidFromUrn } from '../utils/uuidFromUrn';

type CoachingModality = 'individual' | 'group' | undefined;

type UseProgramMatchingArgs = {
  /** The driver-fetched program catalog (raw resource state from useOnboardingResources). */
  prescribedPrograms: ResourceState<PrescribedPrograms>;
  /** Core-owned (read in enrollInProgram); listed in its deps. */
  userDataRef: RefObject<User | undefined>;
  /** Core's route setter — selectProgram routes imperatively. */
  setCurrentRoute: Dispatch<SetStateAction<Href | null>>;
  /** From useOnboardingResources: re-arm teams (group auto-assign) + the resource intent-methods. */
  rearmTeams: () => void;
  markSurveyComplete: () => void;
  markProgramEnrolled: () => void;
};

export type UseProgramMatching = {
  // Public view for the context value.
  prescribedPrograms: PrescribedPrograms | undefined;
  isLoadingPrograms: boolean;
  selectedProgramUuid: string | undefined;
  setSelectedProgram: (programUuid: string | undefined) => void;
  selectProgram: (programUuid: string) => void;
  coachingModality: CoachingModality;
  setCoachingModality: Dispatch<SetStateAction<CoachingModality>>;
  enrollInProgram: (programUuid?: string) => Promise<void>;
  requiresCrisisLine: boolean;
  enterCrisisProgram: (qualifiedProgramUrns: string[]) => void;
  /**
   * Stash a crisis-line outcome captured mid-survey (the dynamic-assessment
   * phq_gad_v1 terminal) WITHOUT routing yet — the remaining survey steps
   * (leftover legacy screeners, state-compliance) still run. goToNextStep
   * consumes it at survey exhaustion: pending urns → enterCrisisProgram instead
   * of markSurveyComplete.
   */
  deferCrisisProgram: (qualifiedProgramUrns: string[]) => void;
  /** The deferred crisis outcome, read by goToNextStep at survey exhaustion. */
  pendingCrisisUrnsRef: RefObject<string[] | undefined>;
  showTestimonialPage: boolean;
  dismissTestimonial: () => void;
  /** Clears the in-flow program picks (per-user reset). */
  reset: () => void;
};

/**
 * Program-matching sub-flow: the user's in-flow picks (selected program +
 * coaching modality + crisis / testimonial routing flags) and the
 * select / enroll / crisis actions. The program catalog itself is a
 * driver-fetched resource (useOnboardingResources) injected here as a value.
 * Extracted verbatim from RulesEngineProvider.
 */
export function useProgramMatching({
  prescribedPrograms,
  userDataRef,
  setCurrentRoute,
  rearmTeams,
  markSurveyComplete,
  markProgramEnrolled,
}: UseProgramMatchingArgs): UseProgramMatching {
  const [selectedProgramUuid, setSelectedProgramUuid] = useState<string | undefined>();
  const [coachingModality, setCoachingModality] = useState<CoachingModality>();
  // A PHQ/GAD result requiring the crisis line (Sparta's setCrisisProgramForOverview).
  // Transient — routes to the crisis screen until the user enrolls.
  const [requiresCrisisLine, setRequiresCrisisLine] = useState(false);
  // Computed at enroll time (from the outcomes flag + program/modality/language —
  // see shouldShowTestimonial); routes to the testimonial page once, before coach
  // matching.
  const [showTestimonialPage, setShowTestimonialPage] = useState(false);

  // Mirror the picks + catalog for the stable enroll/select callbacks.
  const selectedProgramRef = useRef(selectedProgramUuid);
  const coachingModalityRef = useRef(coachingModality);
  const prescribedProgramsRef = useRef<PrescribedPrograms | undefined>(undefined);
  // A crisis outcome deferred until survey exhaustion (see deferCrisisProgram).
  // A ref, not state: nothing renders off it — goToNextStep reads it imperatively.
  const pendingCrisisUrnsRef = useRef<string[] | undefined>(undefined);

  const prescribedProgramsPublic =
    prescribedPrograms.status === 'ready' ? prescribedPrograms.value : undefined;
  const isLoadingPrograms =
    prescribedPrograms.status === 'unset' || prescribedPrograms.status === 'loading';

  // Picking a program clears any prior coaching-modality choice (it's per
  // program). `undefined` resets the selection (the "See other options" path).
  const setSelectedProgram = useCallback((programUuid: string | undefined) => {
    setSelectedProgramUuid(programUuid);
    setCoachingModality(undefined);
  }, []);

  // A PHQ/GAD result requiring the crisis line (Sparta's setCrisisProgramForOverview).
  // Seed the first qualified program as the selection, flag the crisis routing,
  // and mark the survey complete so the driver routes to the crisis screen
  // (which enrolls on Continue). Mirrors Sparta's flag-OFF path — the crisis
  // path intentionally keeps the supportive screen + Continue gate rather than
  // silently auto-joining, unlike the prescribed-program path which now
  // auto-enrolls by default (see the featureFlags note on AUTO_JOIN_PROGRAM).
  const enterCrisisProgram = useCallback(
    (qualifiedProgramUrns: string[]) => {
      // Consume any deferred outcome — entering the crisis flow settles it.
      pendingCrisisUrnsRef.current = undefined;
      setSelectedProgramUuid(uuidFromUrn(qualifiedProgramUrns[0]));
      setCoachingModality(undefined);
      setRequiresCrisisLine(true);
      markSurveyComplete();
    },
    [markSurveyComplete]
  );

  // Hold a dynamic-assessment crisis outcome until the assessment region finishes
  // (leftover legacy screeners + state-compliance still need to run first —
  // in the dynamic arm phq/gad is answered BEFORE state-compliance, unlike
  // Sparta's legacy order where state-compliance precedes it).
  const deferCrisisProgram = useCallback((qualifiedProgramUrns: string[]) => {
    if (qualifiedProgramUrns.length > 0) {
      pendingCrisisUrnsRef.current = qualifiedProgramUrns;
    }
  }, []);

  // Picks a program from the list and routes to the next screen, reading the
  // program's coaching metadata to decide (Sparta's setSelectProgramForOverview):
  //   both group & individual → coaching-method (the user chooses the modality)
  //   single modality         → review (seed the modality from prescribedCoachingMethod)
  const selectProgram = useCallback(
    (programUuid: string) => {
      setSelectedProgramUuid(programUuid);
      const meta = prescribedProgramsRef.current?.programMetaInfo[programMetaInfoKey(programUuid)];
      if (meta?.isGroup && meta?.isIndividual) {
        setCoachingModality(undefined);
        setCurrentRoute('/step/coaching-method-screen');
        return;
      }
      setCoachingModality(meta?.prescribedCoachingMethod ?? undefined);
      setCurrentRoute('/step/full-choice-program-review-screen');
    },
    [setCurrentRoute]
  );

  // Enrolls the user in the selected program (Sparta's createProgramInstance),
  // then flips `programInstances` to ready:true (via markProgramEnrolled) so the
  // driver advances to coach matching — we just created an instance, so no
  // refetch is needed. Throws on failure so the calling screen can surface an
  // error + stop its spinner. `programUuid` defaults to the in-flow selection;
  // the auto-join path passes it explicitly because it enrolls on mount, before
  // the selection ref syncs.
  const enrollInProgram = useCallback(
    async (programUuidArg?: string) => {
      const user = userDataRef.current;
      const programUuid = programUuidArg ?? selectedProgramRef.current;
      const teamCode = user?.customerProfile?.soloTeamCode;
      if (!user || !programUuid || !teamCode) {
        throw new Error('Cannot enroll: missing user, program, or team code.');
      }
      const meta = prescribedProgramsRef.current?.programMetaInfo[programMetaInfoKey(programUuid)];
      const modality = coachingModalityRef.current ?? meta?.prescribedCoachingMethod ?? undefined;
      const instance = await createProgramInstance({
        programUuid,
        userUuid: user.uuid,
        teamCode,
        coachingModality: modality ?? undefined,
      });
      // Decide the testimonial page from the enrolled program (Sparta computes
      // showTestimonialPage in postProgramInstanceRxAction). The resolver shows it
      // once, before coach matching. Evaluate the outcomes flag on the fly here
      // (against the now-identified user) rather than reading a stale auth-time snapshot.
      const program = prescribedProgramsRef.current?.programs.find((p) => p.uuid === programUuid);
      const outcomesVariant = await getFeatureFlag(FEATURE_FLAGS.OUTCOMES_BEFORE_COACH_SELECTION);
      setShowTestimonialPage(
        shouldShowTestimonial({
          outcomesVariant,
          modality,
          programInternalName: program?.internalName,
          preferredLanguage: user.customerProfile?.preferredLanguage,
        })
      );
      markProgramEnrolled();
      // Program-enrollment analytics (Sparta's postProgramInstanceRxAction): the
      // `program-selected` event + person attributes for the enrolled program.
      // (onboarding's ProgramInstance has no teamType, so modality stands in for
      // first_program_modality / team_type.)
      trackEvent('program-selected', {
        uuid: user.uuid,
        payingOrgUrn: user.payingOrganization?.urn,
        teamType: modality,
      });
      void setUserProperties({
        hasProgramInstances: true,
        firstProgramModality: modality,
        firstProgramName: program?.internalName,
        ...(instance?.assignedCoachUuid ? { hasAtLeastOnePrimaryCoach: true } : {}),
      });
      // A group-modality enrollment comes back with an auto-assigned coach
      // (Sparta's `autoAssignedCoach`).
      if (instance?.assignedCoachUuid) {
        // Auto-assignment is also a coach selection: fire `provider-selected`
        // flagged `providerAutoAssigned` so it's distinguishable from the manual
        // coach-matching pick (Sparta's postProgramInstanceRxAction auto-assign
        // branch). The matching `hasAtLeastOnePrimaryCoach` attribute is set in the
        // setUserProperties above for this same case.
        trackEvent('provider-selected', {
          uuid: user.uuid,
          payingOrgUrn: user.payingOrganization?.urn,
          providerUuid: instance.assignedCoachUuid,
          teamCode,
          providerAutoAssigned: true,
        });
        // Re-arm `teams` so the driver re-pulls it and sees the freshly-assigned
        // primary coach → the resolver routes to the terminal, skipping coach
        // matching. Individual programs return no coach, so teams stays empty and
        // the user proceeds to coach matching as before.
        rearmTeams();
      }
    },
    [userDataRef, markProgramEnrolled, rearmTeams]
  );

  // Dismiss the testimonial page (its Continue → "Choose Your Health Coach").
  // Clearing the flag lets the driver re-resolve to coach matching.
  const dismissTestimonial = useCallback(() => {
    setShowTestimonialPage(false);
  }, []);

  const reset = useCallback(() => {
    setSelectedProgramUuid(undefined);
    setCoachingModality(undefined);
    setRequiresCrisisLine(false);
    setShowTestimonialPage(false);
    pendingCrisisUrnsRef.current = undefined;
  }, []);

  // Sync the picks + catalog into refs so the stable callbacks above read fresh values.
  useEffect(() => {
    selectedProgramRef.current = selectedProgramUuid;
    coachingModalityRef.current = coachingModality;
    prescribedProgramsRef.current =
      prescribedPrograms.status === 'ready' ? prescribedPrograms.value : undefined;
  });

  return {
    prescribedPrograms: prescribedProgramsPublic,
    isLoadingPrograms,
    selectedProgramUuid,
    setSelectedProgram,
    selectProgram,
    coachingModality,
    setCoachingModality,
    enrollInProgram,
    requiresCrisisLine,
    enterCrisisProgram,
    deferCrisisProgram,
    pendingCrisisUrnsRef,
    showTestimonialPage,
    dismissTestimonial,
    reset,
  };
}
